/**
 * PATH: apps/hr/src/pages/documents/document-detail/useDocumentDetail.ts
 * PURPOSE: Custom hook encapsulating all state, data fetching, and handlers for document detail
 */

import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hrApi } from '../../../lib/setupApi'
import type { Document, DocumentStatus, ExtractedField } from '../../../types'

type DocumentType = 'license' | 'certification' | 'identification' | 'visa' | 'medical' | 'contract' | 'training' | 'other'

function mapDocumentType(ticketType: string): DocumentType {
  if (!ticketType) return 'other'
  const normalized = ticketType.toLowerCase().replace(/[\s_-]+/g, '_')
  const typeMap: Record<string, DocumentType> = {
    white_card: 'license', driving_license: 'license', drivers_license: 'license',
    driver_license: 'license', ewp_license: 'license', forklift_license: 'license',
    first_aid: 'certification', first_aid_certificate: 'certification',
    police_check: 'identification', wwcc: 'identification', working_with_children_check: 'identification',
    passport: 'identification', visa: 'visa', medical: 'medical', medical_certificate: 'medical',
    trade_certificate: 'certification', certificate: 'certification', license: 'license',
    contract: 'contract', training: 'training',
  }
  return typeMap[normalized] || 'other'
}

function formatDocTypeName(type: string): string {
  if (!type) return 'Unknown'
  return type.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

export const AVAILABLE_TAGS = [
  { name: 'safety', usedByCount: 45 }, { name: 'mandatory', usedByCount: 32 },
  { name: 'trade', usedByCount: 28 }, { name: 'first-aid', usedByCount: 15 },
  { name: 'machinery', usedByCount: 12 }, { name: 'driving', usedByCount: 22 },
  { name: 'immigration', usedByCount: 8 }, { name: 'employment', usedByCount: 18 },
]

export const SUGGESTED_TAGS = ['compliance', 'renewal-required']

export function useDocumentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [editedFields, setEditedFields] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [appliedTags, setAppliedTags] = useState<string[]>([])
  const [tagSearch, setTagSearch] = useState('')
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false)

  const { data: apiResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['document-detail', id],
    queryFn: () => hrApi.getDocumentById(id!),
    enabled: !!id,
  })

  const document: Document | undefined = useMemo(() => {
    const data = apiResponse?.data
    if (!data) return undefined

    const extractedFields: ExtractedField[] = []
    if (data.ocr_extracted_fields) {
      const fields = data.ocr_extracted_fields
      const confidencePercent = Math.round((fields.confidence || 0) * 100)
      if (fields.holderName) extractedFields.push({ name: 'Holder Name', value: fields.holderName, confidence: confidencePercent, isEdited: false })
      if (fields.documentNumber) extractedFields.push({ name: 'Document Number', value: fields.documentNumber, confidence: confidencePercent, isEdited: false })
      if (fields.issueDate) extractedFields.push({ name: 'Issue Date', value: fields.issueDate, confidence: confidencePercent, isEdited: false })
      if (fields.expiryDate) extractedFields.push({ name: 'Expiry Date', value: fields.expiryDate, confidence: confidencePercent, isEdited: false })
      if (fields.documentType) extractedFields.push({ name: 'Document Type', value: fields.documentType, confidence: confidencePercent, isEdited: false })
      if (fields.issuer) extractedFields.push({ name: 'Issuer', value: fields.issuer, confidence: confidencePercent, isEdited: false })
    }

    const ocrDocType = data.ocr_extracted_fields?.documentType
    const rawDocType = ocrDocType || data.ticket_type || data.document_type || ''

    return {
      id: data.id,
      documentType: mapDocumentType(rawDocType) as any,
      documentTypeName: formatDocTypeName(rawDocType),
      documentNumber: data.ticket_number || data.ocr_extracted_fields?.documentNumber || '',
      documentName: data.document_name || data.file_name || 'Unknown Document',
      employeeId: data.worker_id,
      employeeName: data.employee_name || 'Unknown Employee',
      status: data.document_status as DocumentStatus,
      issueDate: data.issued_date || data.ocr_extracted_fields?.issueDate,
      expiryDate: data.expiry_date,
      daysUntilExpiry: data.days_until_expiry,
      createdAt: data.created_at,
      updatedAt: data.updated_at || data.created_at,
      confidenceScore: data.ocr_confidence ? Math.round(data.ocr_confidence * 100) : undefined,
      extractedFields,
      fileName: data.file_name,
      fileUrl: data.file_url || '',
      fileType: data.content_type || data.file_type || '',
      fileSize: data.file_size,
      tags: data.tags || [],
      issuer: data.ocr_extracted_fields?.issuingAuthority,
    }
  }, [apiResponse])

  useEffect(() => { if (document?.tags) setAppliedTags(document.tags) }, [document?.tags])

  const workerId = apiResponse?.data?.worker_id
  const isExpired = document?.daysUntilExpiry !== undefined && document.daysUntilExpiry <= 0
  const isExpiringSoon = document?.daysUntilExpiry !== undefined && document.daysUntilExpiry > 0 && document.daysUntilExpiry <= 30

  const handleFieldEdit = (fieldName: string, value: string) => {
    setEditedFields(prev => ({ ...prev, [fieldName]: value }))
  }

  const handleSaveFields = async () => {
    if (!workerId) return
    setIsSaving(true)
    try {
      await hrApi.updateDocument(workerId, id!, editedFields)
      await refetch()
      setIsEditing(false)
      setEditedFields({})
    } catch (err) { console.error('Failed to save fields:', err) }
    finally { setIsSaving(false) }
  }

  const handleApprove = async () => {
    if (!workerId) return
    setIsApproving(true)
    try {
      await hrApi.approveDocument(workerId, id!)
      queryClient.invalidateQueries({ queryKey: ['all-documents'] })
      navigate('/documents')
    } catch (err) { console.error('Failed to approve document:', err) }
    finally { setIsApproving(false) }
  }

  const handleReject = async (reason: string) => {
    if (!workerId) return
    setIsRejecting(true)
    try {
      await hrApi.rejectDocument(workerId, id!, reason)
      queryClient.invalidateQueries({ queryKey: ['all-documents'] })
      setShowRejectModal(false)
      navigate('/documents')
    } catch (err) { console.error('Failed to reject document:', err) }
    finally { setIsRejecting(false) }
  }

  const handleDelete = async () => {
    if (!workerId) return
    setIsDeleting(true)
    try {
      await hrApi.deleteDocument(workerId, id!)
      queryClient.invalidateQueries({ queryKey: ['all-documents'] })
      navigate('/documents')
    } catch (err) { console.error('Failed to delete:', err) }
    finally { setIsDeleting(false) }
  }

  const handleApplyTag = (tag: string) => {
    if (!appliedTags.includes(tag)) setAppliedTags(prev => [...prev, tag])
    setTagSearch('')
    setShowTagDropdown(false)
  }

  const handleRemoveTag = (tag: string) => setAppliedTags(prev => prev.filter(t => t !== tag))

  const handleDownload = () => {
    if (!document?.fileUrl) return
    const link = window.document.createElement('a')
    link.href = document.fileUrl
    link.download = document.fileName || 'document'
    link.target = '_blank'
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
  }

  return {
    id, document, isLoading, error, navigate,
    isEditing, setIsEditing, editedFields, setEditedFields,
    isSaving, showRejectModal, setShowRejectModal,
    showDeleteModal, setShowDeleteModal, isDeleting,
    appliedTags, tagSearch, setTagSearch,
    showTagDropdown, setShowTagDropdown,
    showCreateRequestModal, setShowCreateRequestModal,
    workerId, isExpired, isExpiringSoon,
    handleFieldEdit, handleSaveFields, handleApprove,
    handleReject, handleDelete, handleApplyTag,
    handleRemoveTag, handleDownload,
  }
}
